import { ViewChild, Directive } from '@angular/core';
import { isArray } from '../../../models/utils';
import { ContextMenuOption } from '../../../models/context-menu-option';
import { DrDocument } from '../../../models/dr-document';
import { DrRequestInfo } from '../../../models/dr-request-info';
import { Endpoint, EntityType, HelpIndex, UpdateStatusType } from '../../../models/enums';
import { GenericColDef, GridContext, GridRowSelectionType } from '../../../models/grid-context';
import { GenericTreeNode } from '../../generic-tree/generic-tree-node';
import { MainContentComponent } from '../../main-content/main-content.component';
import { DocumentListColumnInfo, ReorderedDocumentListColumnInfo } from './document-list-column-info';
import { DocumentListContextMenuOptionsInfo } from './document-list-context-menu-options-info';
import { DocumentListHeaderButtonOptionsInfo } from './document-list-header-button-options-info';

@Directive()
export class DocumentListData {
    @ViewChild('dropdownListComparison', { static: true }) dropdownListComparison: MainContentComponent;

    isValid = false;
    gridName = 'Documents';
    automationId = 'document-list';
    helpIndex = HelpIndex.project_data_doc_lists_dlg;
    updateStatusType: UpdateStatusType;
    columnDefs: Array<GenericColDef>;
    rowSelection = GridRowSelectionType.multipleCheckboxWithRowClickSelection;
    listingHandleFieldName = 'handle';
    hideDuplicatesVisible = true;
    hasHeaderTemplate = true;
    pagerVisible = true;
    isBusyOnInit = false;
    requestInfo = new DrRequestInfo({
        endpoint: Endpoint.viewManager_listDocuments,
        responseName: 'documents',
        data: {
            selectedAttributes: ['size', 'author', 'attachments', 'date_or_lastmodifiedtime', 'filetype', 'filepath', 'score',
                'group', 'createdtime', 'datescanned', 'cluster_view', 'from', 'to', 'subject', 'cc', 'to_name', 'from_name',
                'sent', 'received', 'thread_handle', 'family_handle', 'duplicateCount', 'trackchanges', 'hiddendata', 'docannotations',
                'docclass', 'searchreporttype', 'percentCommonTerms', 'docnum', 'dateprimary', 'smf_message_body', 'smf_datatype',
                'add_to_workingset', 'imagepdfpath', 'importpath', 'add_history_comments', 'add_history_username', 'add_history_time',
                'to_identifier', 'from_identifier', 'parsingstatus'
            ],
            suppressDuplicates: false,
            filters: null
        }
    });
    additionalAttributes: Array<string>;
    contextMenuOptions: Array<ContextMenuOption>;
    documentHeaderButtonOptions: Array<ContextMenuOption>;
    gridContext: GridContext;

    constructor(documentListComponent: any, node: GenericTreeNode, value: Partial<DocumentListData>) {
        if (documentListComponent && node && node.data && value) {
            Object.assign(this, value);

            // add auto ids
            if (this.columnDefs) {
                this.columnDefs.forEach(colDef => {
                    colDef.automationId = this.automationId;
                });
            }

            // check request data
            let view = node.data;
            if (node.data.view) {
                view = node.data.view;
            }

            if (view[this.listingHandleFieldName] || view.handle) {
                this.isValid = true;

                this.requestInfo.data.viewHandle = view[this.listingHandleFieldName] ? view[this.listingHandleFieldName] : view.handle;
                this.requestInfo.data.skipCalculation = view.documentCount > 100000 || (view.documentCount === 0 && view.busy);
            }
            if (isArray(this.requestInfo.data.selectedAttributes) && isArray(this.additionalAttributes)) {
                this.requestInfo.data.selectedAttributes = this.requestInfo.data.selectedAttributes.concat(this.additionalAttributes);
            }
            // set context menu options
            if (this.contextMenuOptions) {
                this.contextMenuOptions.forEach(option => {
                    if (!option.divider) {
                        option.click = (item: DrDocument) => documentListComponent.onDocumentContextMenuOption(item, option.name);
                        option.enabled = (item: DrDocument) => documentListComponent.isDocumentContextMenuOptionEnabled(item, option.name);
                        option.visible = (item: DrDocument) => documentListComponent.isDocumentContextMenuOptionVisible(item, option.name);
                    }
                });
            }

            // set header button menu options
            if (this.documentHeaderButtonOptions) {
                this.documentHeaderButtonOptions.forEach(option => {
                    if (!option.divider) {
                        option.click = () => documentListComponent.onDocumentHeaderButtonOption(option.name);
                        option.enabled = () => documentListComponent.isDocumentHeaderButtonOptionEnabled(option.name);
                        option.visible = () => documentListComponent.isDocumentHeaderButtonOptionVisible(option.name);
                    }
                });
            }

            if (documentListComponent.isDocumentViewerDocList) {
                this.rowSelection = GridRowSelectionType.single;
                this.columnDefs = this.columnDefs.filter(col => col.colId !== 'checkbox');
                this.requestInfo.data.selectedAttributes = this.requestInfo.data.selectedAttributes.concat(['stored_image']);
                if (this.gridName === 'Exception List') {
                    this.requestInfo.data.filters = [{ attribute: 'viewlistattribute', value: 'exceptions' }];
                    this.requestInfo.data.selectedAttributes.push('parsingstatus');
                    if (this.gridContext) {
                        this.gridContext.sortEnabled = false;
                    }
                }
                this.gridName = 'Documents';
                this.automationId = 'document-viewer-documents';
                this.hasHeaderTemplate = false;
                documentListComponent.cellDoubleClickedCallback = null;
            } else if (documentListComponent.isDocumentViewerFamilyList) {
                this.rowSelection = GridRowSelectionType.single;
                this.columnDefs = this.columnDefs.filter(col => col.colId !== 'checkbox');
                this.hideDuplicatesVisible = false;
                this.requestInfo.endpoint = Endpoint.viewManager_getDocumentAttachments;
                this.requestInfo.data = {
                    handle: this.requestInfo.data.viewHandle ? this.requestInfo.data.viewHandle : view.handle,
                    selectedAttributes: this.requestInfo.data.selectedAttributes.concat(['stored_image'])
                };
                if (this.gridContext) {
                    this.gridContext.sortBy = null;
                    this.gridContext.sortEnabled = false;
                }
                this.gridName = 'Family';
                this.automationId = 'document-viewer-family';
                this.pagerVisible = false;
                this.hasHeaderTemplate = false;
                documentListComponent.cellDoubleClickedCallback = null;
            } else if (documentListComponent.isDocumentViewerThreadList) {
                if (documentListComponent.commonService?.documentViewerOpenContext?.isShortMessageThreads) {
                    this.columnDefs = DocumentListColumnInfo.get(EntityType.shortMessageThreadDocList);
                } else {
                    this.columnDefs = this.columnDefs.filter(col => col.colId !== 'checkbox');
                }
                this.rowSelection = GridRowSelectionType.single;
                this.hideDuplicatesVisible = false;
                this.requestInfo = null;
                if (this.gridContext) {
                    this.gridContext.sortBy = null;
                    this.gridContext.sortEnabled = false;
                }
                this.gridName = 'Threads';
                this.automationId = 'document-viewer-threads';
                this.pagerVisible = true;
                this.hasHeaderTemplate = false;
                this.isBusyOnInit = true;
                documentListComponent.cellDoubleClickedCallback = null;
            } else if (documentListComponent.isMainEmailThreads) {
                this.columnDefs = DocumentListColumnInfo.get(EntityType.emailThread);
                this.contextMenuOptions = DocumentListContextMenuOptionsInfo.get(EntityType.emailThread);
                this.documentHeaderButtonOptions = DocumentListHeaderButtonOptionsInfo.get(EntityType.emailThread);
                this.hideDuplicatesVisible = false;
                this.requestInfo = null;
                if (this.gridContext) {
                    this.gridContext.sortBy = 'dateprimary';
                    this.gridContext.sortEnabled = false;
                    this.gridContext.sortAscending = true;
                    this.gridContext.groupItems = (sortBy: string) => ['sent', 'received', 'dateprimary'].includes(this.gridContext.sortBy);
                }
                this.gridName = 'Email Threads';
                this.pagerVisible = true;
                this.hasHeaderTemplate = true;
                this.isBusyOnInit = true;
                this.helpIndex = HelpIndex.myedisc_email_threads_tab_dlg;
                this.updateStatusType = UpdateStatusType.WORKING_SET;
                this.hideCheckboxesByPermissions();
            } else if (documentListComponent.isShortMessageThreads) {
                this.columnDefs = DocumentListColumnInfo.get(EntityType.shortMessageThread);
                this.contextMenuOptions = DocumentListContextMenuOptionsInfo.get(EntityType.emailThread);
                this.documentHeaderButtonOptions = DocumentListHeaderButtonOptionsInfo.get(EntityType.emailThread);
                this.hideDuplicatesVisible = false;
                this.requestInfo = null;
                if (this.gridContext) {
                    this.gridContext.sortBy = 'dateprimary';
                    this.gridContext.sortEnabled = false;
                    this.gridContext.sortAscending = true;
                    this.gridContext.groupItems = (sortBy: string) => ['sent', 'received', 'dateprimary'].includes(this.gridContext.sortBy);
                }
                this.gridName = 'Short Message Threads';
                this.pagerVisible = true;
                this.hasHeaderTemplate = true;
                this.isBusyOnInit = true;
                this.helpIndex = HelpIndex.myedisc_email_threads_tab_dlg;
                this.updateStatusType = UpdateStatusType.WORKING_SET;
                this.hideCheckboxesByPermissions();
            } else if (documentListComponent.isDocumentViewerExceptionList) {
                this.columnDefs = DocumentListColumnInfo.get(EntityType.exceptionView);
                this.contextMenuOptions = DocumentListContextMenuOptionsInfo.get(EntityType.exceptionView);
                this.documentHeaderButtonOptions = DocumentListHeaderButtonOptionsInfo.get(EntityType.exceptionView);
                this.hideDuplicatesVisible = false;
                this.requestInfo.data.filters = [{ attribute: 'viewlistattribute', value: 'exceptions' }];
                this.requestInfo.data.skipCalculation = false;
                this.requestInfo.data.selectedAttributes.push('parsingstatus');
                if (this.gridContext) {
                    this.gridContext.sortBy = 'docnum';
                    this.gridContext.sortEnabled = false;
                    this.gridContext.sortAscending = true;
                    this.gridContext.groupItems = (sortBy: string) => ['sent', 'received', 'dateprimary'].includes(this.gridContext.sortBy);
                }
                this.gridName = 'Exceptions';
                this.automationId = 'document-viewer-exception-list';
                this.pagerVisible = true;
                this.hasHeaderTemplate = true;
                this.isBusyOnInit = true;
            }
        }
    }

    hideCheckboxesByPermissions(): void {
        let hasVisibleOptions = false;
        if (this.documentHeaderButtonOptions && this.documentHeaderButtonOptions.length > 0) {
            const visibleOptions = this.documentHeaderButtonOptions.filter(o => !o.divider && (Boolean)(o.isVisible(o)));
            hasVisibleOptions = visibleOptions && visibleOptions.length > 0;
        }
        if (this.columnDefs.find(c => c.colId === 'checkbox')) {
            if (!hasVisibleOptions) {
                this.columnDefs = this.columnDefs.filter(col => col.colId !== 'checkbox');
                // hide header "Documents" menu when the user does not have any visible options due to lack of permissions
                this.documentHeaderButtonOptions = null;
            }
        }
    }
}

export const DocumentListInfo: ReadonlyMap<EntityType, (context: any, node: GenericTreeNode) =>
    DocumentListData> = new Map<EntityType, (context: any, node: GenericTreeNode) => DocumentListData>([
        [EntityType.import, (context, node) => new DocumentListData(context, node, {
            gridName: 'Import Documents',
            automationId: 'import-document-list',
            updateStatusType: UpdateStatusType.CORPUS,
            helpIndex: HelpIndex.myedisc_doc_lists_dlg,
            columnDefs: DocumentListColumnInfo.get(EntityType.import),
            listingHandleFieldName: 'corpusViewHandle',
            contextMenuOptions: DocumentListContextMenuOptionsInfo.get(EntityType.import),
            documentHeaderButtonOptions: DocumentListHeaderButtonOptionsInfo.get(EntityType.import),
            gridContext: new GridContext({
                sortBy: 'docnum',
                sortAscending: true,
                groupItems: (sortBy: string) => ['sent', 'received', 'dateprimary'].includes(sortBy)
            }),
            additionalAttributes: ['importpath', 'parsingstatus']
        })],
        [EntityType.export, (context, node) => new DocumentListData(context, node, {
            gridName: 'Export Documents',
            automationId: 'export-document-list',
            updateStatusType: UpdateStatusType.EXPORT_VIEW,
            helpIndex: HelpIndex.exports_ediscexports_stream_docs_dlg,
            columnDefs: ReorderedDocumentListColumnInfo.get(EntityType.export) ? ReorderedDocumentListColumnInfo.get(EntityType.export) : DocumentListColumnInfo.get(EntityType.export),
            contextMenuOptions: DocumentListContextMenuOptionsInfo.get(EntityType.export),
            documentHeaderButtonOptions: DocumentListHeaderButtonOptionsInfo.get(EntityType.export),
            gridContext: new GridContext({
                sortBy: 'docid',
                sortAscending: true,
                groupItems: (sortBy: string) => ['docid'].includes(sortBy)
            }),
            additionalAttributes: ['docid', 'volume', 'responsive']
        })],
        [EntityType.exportVolume, (context, node) => new DocumentListData(context, node, {
            gridName: 'Export Volume Documents',
            automationId: 'export-volume-document-list',
            updateStatusType: UpdateStatusType.EXPORT_VIEW,
            helpIndex: HelpIndex.exports_ediscexports_vol_docs_dlg,
            columnDefs: ReorderedDocumentListColumnInfo.get(EntityType.export) ? ReorderedDocumentListColumnInfo.get(EntityType.export) : DocumentListColumnInfo.get(EntityType.export),
            contextMenuOptions: DocumentListContextMenuOptionsInfo.get(EntityType.exportVolume),
            documentHeaderButtonOptions: DocumentListHeaderButtonOptionsInfo.get(EntityType.exportVolume),
            gridContext: new GridContext({
                sortBy: 'docid',
                sortAscending: true,
                groupItems: (sortBy: string) => ['docid', 'docnum'].includes(sortBy)
            }),
            additionalAttributes: ['docid', 'volume', 'responsive']
        })],
        [EntityType.projectData, (context, treeItem) => new DocumentListData(context, treeItem, {
            gridName: 'Project Data Documents',
            automationId: 'project-data-document-list',
            updateStatusType: UpdateStatusType.WORKING_SET,
            columnDefs: ReorderedDocumentListColumnInfo.get(EntityType.projectData) ? ReorderedDocumentListColumnInfo.get(EntityType.projectData) : DocumentListColumnInfo.get(EntityType.projectData),
            contextMenuOptions: DocumentListContextMenuOptionsInfo.get(EntityType.projectData),
            documentHeaderButtonOptions: DocumentListHeaderButtonOptionsInfo.get(EntityType.projectData),
            gridContext: new GridContext({
                sortBy: 'dateprimary',
                sortAscending: true,
                groupItems: (sortBy: string) => ['sent', 'received', 'dateprimary'].includes(sortBy)
            })
        })],
        [EntityType.folder, (context, treeItem) => new DocumentListData(context, treeItem, {
            gridName: 'Folder Documents',
            automationId: 'folder-document-list',
            updateStatusType: UpdateStatusType.DYNAMIC_FOLDER_VIEW,
            columnDefs: ReorderedDocumentListColumnInfo.get(EntityType.projectData) ? ReorderedDocumentListColumnInfo.get(EntityType.projectData) : DocumentListColumnInfo.get(EntityType.projectData),
            contextMenuOptions: DocumentListContextMenuOptionsInfo.get(EntityType.projectData),
            documentHeaderButtonOptions: DocumentListHeaderButtonOptionsInfo.get(EntityType.projectData),
            gridContext: new GridContext({
                sortBy: 'dateprimary',
                sortAscending: true,
                groupItems: (sortBy: string) => ['sent', 'received', 'dateprimary'].includes(sortBy)
            })
        })],
        [EntityType.tagView, (context, treeItem) => new DocumentListData(context, treeItem, {
            gridName: 'Tag Documents',
            automationId: 'tag-document-list',
            updateStatusType: UpdateStatusType.TAG_VIEW,
            columnDefs: ReorderedDocumentListColumnInfo.get(EntityType.projectData) ? ReorderedDocumentListColumnInfo.get(EntityType.projectData) : DocumentListColumnInfo.get(EntityType.projectData),
            contextMenuOptions: DocumentListContextMenuOptionsInfo.get(EntityType.projectData),
            documentHeaderButtonOptions: DocumentListHeaderButtonOptionsInfo.get(EntityType.projectData),
            gridContext: new GridContext({
                sortBy: 'dateprimary',
                sortAscending: true,
                groupItems: (sortBy: string) => ['sent', 'received', 'dateprimary'].includes(sortBy)
            })
        })],
        [EntityType.custodian, (context, treeItem) => new DocumentListData(context, treeItem, {
            gridName: 'Custodian Documents',
            automationId: 'custodian-document-list',
            updateStatusType: UpdateStatusType.CUSTODIAN,
            columnDefs: ReorderedDocumentListColumnInfo.get(EntityType.projectData) ? ReorderedDocumentListColumnInfo.get(EntityType.projectData) : DocumentListColumnInfo.get(EntityType.projectData),
            contextMenuOptions: DocumentListContextMenuOptionsInfo.get(EntityType.projectData),
            documentHeaderButtonOptions: DocumentListHeaderButtonOptionsInfo.get(EntityType.projectData),
            gridContext: new GridContext({
                sortBy: 'dateprimary',
                sortAscending: true,
                groupItems: (sortBy: string) => ['sent', 'received', 'dateprimary'].includes(sortBy)
            })
        })],
        [EntityType.mediaID, (context, treeItem) => new DocumentListData(context, treeItem, {
            gridName: 'MediaID Documents',
            automationId: 'mediaID-document-list',
            updateStatusType: UpdateStatusType.MEDIAID,
            columnDefs: ReorderedDocumentListColumnInfo.get(EntityType.projectData) ? ReorderedDocumentListColumnInfo.get(EntityType.projectData) : DocumentListColumnInfo.get(EntityType.projectData),
            contextMenuOptions: DocumentListContextMenuOptionsInfo.get(EntityType.projectData),
            documentHeaderButtonOptions: DocumentListHeaderButtonOptionsInfo.get(EntityType.projectData),
            gridContext: new GridContext({
                sortBy: 'dateprimary',
                sortAscending: true,
                groupItems: (sortBy: string) => ['sent', 'received', 'dateprimary'].includes(sortBy)
            })
        })],
        [EntityType.batch, (context, treeItem) => new DocumentListData(context, treeItem, {
            gridName: 'Batch Documents',
            automationId: 'batch-document-list',
            updateStatusType: UpdateStatusType.BATCH,
            columnDefs: ReorderedDocumentListColumnInfo.get(EntityType.projectData) ? ReorderedDocumentListColumnInfo.get(EntityType.projectData) : DocumentListColumnInfo.get(EntityType.projectData),
            contextMenuOptions: DocumentListContextMenuOptionsInfo.get(EntityType.projectData),
            documentHeaderButtonOptions: DocumentListHeaderButtonOptionsInfo.get(EntityType.projectData),
            gridContext: new GridContext({
                sortBy: 'dateprimary',
                sortAscending: true,
                groupItems: (sortBy: string) => ['sent', 'received', 'dateprimary'].includes(sortBy)
            })
        })],
        // Non Import & Data set searches
        [EntityType.searchResult, (context, treeItem) => new DocumentListData(context, treeItem, {
            gridName: 'Search Results',
            automationId: 'search-results-document-list',
            updateStatusType: UpdateStatusType.RESULT_VIEW,
            helpIndex: HelpIndex.project_data_doc_search_results_dlg,
            columnDefs: DocumentListColumnInfo.get(EntityType.searchResult),
            contextMenuOptions: DocumentListContextMenuOptionsInfo.get(EntityType.searchResult),
            documentHeaderButtonOptions: DocumentListHeaderButtonOptionsInfo.get(EntityType.searchResult),
            gridContext: new GridContext({
                sortBy: 'score',
                sortAscending: false,
                groupItems: (sortBy: string) => ['sent', 'received', 'dateprimary'].includes(sortBy)
            })
        })],
        [EntityType.searchResultExportErrors, (context, treeItem) => new DocumentListData(context, treeItem, {
            gridName: 'Search Results',
            automationId: 'search-results-document-list',
            updateStatusType: UpdateStatusType.RESULT_VIEW,
            helpIndex: HelpIndex.project_data_doc_search_results_dlg,
            columnDefs: DocumentListColumnInfo.get(EntityType.searchResultExportErrors),
            contextMenuOptions: DocumentListContextMenuOptionsInfo.get(EntityType.searchResult),
            documentHeaderButtonOptions: DocumentListHeaderButtonOptionsInfo.get(EntityType.searchResult),
            gridContext: new GridContext({
                sortBy: 'score',
                sortAscending: false,
                groupItems: (sortBy: string) => ['sent', 'received', 'dateprimary'].includes(sortBy)
            })
        })],
        [EntityType.sampleSet, (context, treeItem) => new DocumentListData(context, treeItem, {
            gridName: 'Search Results',
            automationId: 'search-results-document-list',
            updateStatusType: UpdateStatusType.RESULT_VIEW,
            helpIndex: HelpIndex.project_data_doc_search_results_dlg,
            columnDefs: DocumentListColumnInfo.get(EntityType.sampleSet),
            contextMenuOptions: DocumentListContextMenuOptionsInfo.get(EntityType.searchResult),
            documentHeaderButtonOptions: DocumentListHeaderButtonOptionsInfo.get(EntityType.searchResult),
            gridContext: new GridContext({
                sortBy: 'dateprimary',
                sortAscending: true,
                groupItems: (sortBy: string) => ['sent', 'received', 'dateprimary'].includes(sortBy)
            })
        })],
        // Import & Data set searches
        [EntityType.searchResultImport, (context, treeItem) => new DocumentListData(context, treeItem, {
            gridName: 'Search Results',
            automationId: 'search-results-document-list',
            updateStatusType: UpdateStatusType.RESULT_VIEW,
            helpIndex: HelpIndex.myedisc_doc_search_results_dlg,
            columnDefs: DocumentListColumnInfo.get(EntityType.searchResultImport),
            contextMenuOptions: DocumentListContextMenuOptionsInfo.get(EntityType.searchResult),
            documentHeaderButtonOptions: DocumentListHeaderButtonOptionsInfo.get(EntityType.searchResultImport),
            gridContext: new GridContext({
                sortBy: 'score',
                sortAscending: false,
                groupItems: (sortBy: string) => ['sent', 'received', 'dateprimary'].includes(sortBy)
            })
        })],
        [EntityType.similaritySearchResult, (context, treeItem) => new DocumentListData(context, treeItem, {
            gridName: 'Similarity Search Results',
            automationId: 'search-results-document-list',
            updateStatusType: UpdateStatusType.RESULT_VIEW,
            helpIndex: HelpIndex.project_data_doc_search_results_dlg,
            columnDefs: ReorderedDocumentListColumnInfo.get(EntityType.similaritySearchResult) ? ReorderedDocumentListColumnInfo.get(EntityType.similaritySearchResult) : DocumentListColumnInfo.get(EntityType.similaritySearchResult),
            contextMenuOptions: DocumentListContextMenuOptionsInfo.get(EntityType.searchResult),
            documentHeaderButtonOptions: DocumentListHeaderButtonOptionsInfo.get(EntityType.searchResult),
            gridContext: new GridContext({
                sortBy: 'score',
                sortAscending: false,
                groupItems: (sortBy: string) => ['sent', 'received', 'dateprimary'].includes(sortBy)
            })
        })],
        [EntityType.exactOrContentDuplicatesSearchResult, (context, treeItem) => new DocumentListData(context, treeItem, {
            gridName: 'Exact or Content Duplicates Search Results',
            automationId: 'search-results-document-list',
            updateStatusType: UpdateStatusType.RESULT_VIEW,
            helpIndex: HelpIndex.project_data_doc_search_results_dlg,
            columnDefs: ReorderedDocumentListColumnInfo.get(EntityType.exactOrContentDuplicatesSearchResult) ? ReorderedDocumentListColumnInfo.get(EntityType.exactOrContentDuplicatesSearchResult) : DocumentListColumnInfo.get(EntityType.exactOrContentDuplicatesSearchResult),
            contextMenuOptions: DocumentListContextMenuOptionsInfo.get(EntityType.searchResult),
            documentHeaderButtonOptions: DocumentListHeaderButtonOptionsInfo.get(EntityType.searchResult),
            gridContext: new GridContext({
                sortBy: 'group',
                sortAscending: false,
                groupItems: (sortBy: string) => ['sent', 'received', 'dateprimary'].includes(sortBy)
            })
        })],
        [EntityType.clusterView, (context, treeItem) => new DocumentListData(context, treeItem, {
            gridName: 'Cluster Documents',
            automationId: 'cluster-document-list',
            updateStatusType: UpdateStatusType.CLUSTER_VIEW,
            columnDefs: DocumentListColumnInfo.get(EntityType.clusterView),
            listingHandleFieldName: 'handle',
            helpIndex: HelpIndex.myedisc_clusters_tab_dlg,
            contextMenuOptions: DocumentListContextMenuOptionsInfo.get(EntityType.clusterView),
            documentHeaderButtonOptions: DocumentListHeaderButtonOptionsInfo.get(EntityType.clusterView),
            gridContext: new GridContext({
                sortBy: 'score',
                sortAscending: false,
                groupItems: (sortBy: string) => ['sent', 'received', 'dateprimary'].includes(sortBy)
            })
        })],
        [EntityType.comparison, (context, treeItem) => new DocumentListData(context, treeItem, {
            gridName: 'Comparison',
            automationId: 'comparison-document-list',
            hideDuplicatesVisible: true,
            listingHandleFieldName: 'docListHandle',
            helpIndex: HelpIndex.myedisc_comparison_view_dlg,
            updateStatusType: UpdateStatusType.COMPARISON,
            columnDefs: ReorderedDocumentListColumnInfo.get(EntityType.projectData) ? ReorderedDocumentListColumnInfo.get(EntityType.projectData) : DocumentListColumnInfo.get(EntityType.projectData),
            contextMenuOptions: DocumentListContextMenuOptionsInfo.get(EntityType.comparison),
            documentHeaderButtonOptions: DocumentListHeaderButtonOptionsInfo.get(EntityType.comparison),
            gridContext: new GridContext({
                sortBy: 'dateprimary',
                sortAscending: true,
                groupItems: (sortBy: string) => ['sent', 'received', 'dateprimary'].includes(sortBy)
            })
        })],
        [EntityType.savedSearch, (context, treeItem) => new DocumentListData(context, treeItem, {
            gridName: 'Saved Search',
            automationId: 'saved-search-document-list',
            updateStatusType: UpdateStatusType.SAVED_SEARCH,
            helpIndex: HelpIndex.project_data_doc_search_results_dlg,
            columnDefs: DocumentListColumnInfo.get(EntityType.savedSearch),
            contextMenuOptions: DocumentListContextMenuOptionsInfo.get(EntityType.projectData),
            documentHeaderButtonOptions: DocumentListHeaderButtonOptionsInfo.get(EntityType.savedSearch),
            gridContext: new GridContext({
                sortBy: 'score',
                sortAscending: false,
                groupItems: (sortBy: string) => ['sent', 'received', 'dateprimary'].includes(sortBy)
            })
        })],
        [EntityType.exceptionView, (context, treeItem) => new DocumentListData(context, treeItem, {
            gridName: 'Exception List',
            automationId: 'exception-document-list',
            hideDuplicatesVisible: true,
            helpIndex: HelpIndex.myedisc_doc_lists_dlg,
            columnDefs: DocumentListColumnInfo.get(EntityType.exceptionView),
            contextMenuOptions: DocumentListContextMenuOptionsInfo.get(EntityType.exceptionView),
            documentHeaderButtonOptions: DocumentListHeaderButtonOptionsInfo.get(EntityType.exceptionView),
            updateStatusType: UpdateStatusType.WORKING_SET,
            gridContext: new GridContext({
                sortBy: 'docnum',
                sortAscending: true,
                groupItems: (sortBy: string) => ['sent', 'received', 'dateprimary'].includes(sortBy)
            })
        })],
        [EntityType.discardPile, (context, treeItem) => new DocumentListData(context, treeItem, {
            gridName: 'Discard Pile Documents',
            automationId: 'discard-pile-document-list',
            helpIndex: HelpIndex.myedisc_discard_pile_dlg,
            updateStatusType: UpdateStatusType.WORKING_SET,
            columnDefs: DocumentListColumnInfo.get(EntityType.discardPile),
            contextMenuOptions: DocumentListContextMenuOptionsInfo.get(EntityType.discardPile),
            documentHeaderButtonOptions: DocumentListHeaderButtonOptionsInfo.get(EntityType.discardPile),
            gridContext: new GridContext({
                sortBy: 'add_to_workingset',
                sortAscending: false,
            })
        })],
         [EntityType.searchResultDiscardPile, (context, treeItem) => new DocumentListData(context, treeItem, {
            gridName: 'Search Results',
            automationId: 'search-results-document-list',
            updateStatusType: UpdateStatusType.RESULT_VIEW,
            helpIndex: HelpIndex.project_data_doc_search_results_dlg,
            columnDefs: DocumentListColumnInfo.get(EntityType.searchResult),
            contextMenuOptions: DocumentListContextMenuOptionsInfo.get(EntityType.searchResult),
            documentHeaderButtonOptions: DocumentListHeaderButtonOptionsInfo.get(EntityType.discardPile),
            gridContext: new GridContext({
                sortBy: 'score',
                sortAscending: false,
                groupItems: (sortBy: string) => ['sent', 'received', 'dateprimary'].includes(sortBy)
            })
        })],
    ]);
